import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { Box, Typography } from "@mui/material";
import { MODULE_TYPE_LABELS } from "entities/modules/constants";
import {
  PageModuleDataWithPageId,
  PageModuleStatus,
} from "entities/pages/types";
import ModuleStatusBadge from "modules/brands/pages/ModulesContainer/components/PageModules/components/ModuleStatusBadge";
import React from "react";
import { COLOR_BLUE, COLOR_GREY_6, COLOR_WHITE } from "shared/theme/colors";
import { StarsBadgeIcon } from "shared/theme/icons/StarsBadgeIcon";
import { formatScheduledDate } from "shared/utils/formatScheduledDate";
import Chip from "../Chip";
import DropdownList from "../DropdownList";
import SpaceBetweenCenter from "../Layout/components/SpaceBetweenCenter";
import ModuleHeaderSkeleton from "../ModuleSkeleton/components/ModuleHeaderSkeleton";
import {
  GradientText,
  StyledAIGradientBox,
  AnimatedTypingText,
  ChipWrapper,
  AnimatedRightItem,
} from "./styled";

interface ModuleTileHeaderProps {
  module: PageModuleDataWithPageId;
  onEditComponent: (module: PageModuleDataWithPageId) => void;
  onDeleteComponent: (module: PageModuleDataWithPageId) => void;
  isLoading?: boolean;
  onUpdateStatus: (params: {
    moduleId: string;
    status: PageModuleStatus;
  }) => void;
  isNewlyGenerated?: boolean;
}

const ModuleTileHeader: React.FC<ModuleTileHeaderProps> = ({
  module,
  onEditComponent,
  onDeleteComponent,
  isLoading = false,
  onUpdateStatus,
  isNewlyGenerated = false,
}) => {
  if (isLoading) {
    return <ModuleHeaderSkeleton />;
  }

  const actionItems = [
    {
      title: "Edit",
      style: "normal" as const,
      onClick: () => onEditComponent(module),
    },
    {
      title: "Delete",
      style: "remove" as const,
      onClick: () => onDeleteComponent(module),
    },
  ];

  const chipText = module.scheduledEndAt
    ? formatScheduledDate(module.scheduledStartAt, module.scheduledEndAt)
    : "Unscheduled";

  return (
    <SpaceBetweenCenter>
      <Box sx={{ display: "flex", alignItems: "center", gap: "16px" }}>
        <AnimatedTypingText variant="h3" isNewlyGenerated={isNewlyGenerated}>
          {module.name}
        </AnimatedTypingText>
        <ChipWrapper isNewlyGenerated={isNewlyGenerated}>
          <Chip
            borderColor={COLOR_GREY_6}
            bgColor={COLOR_WHITE}
            title={MODULE_TYPE_LABELS[module.type]}
          />
        </ChipWrapper>
        {module.isAiGenerated && (
          <StyledAIGradientBox isNewlyGenerated={isNewlyGenerated}>
            <GradientText>Lobby Agent</GradientText>
            <StarsBadgeIcon size={16} />
          </StyledAIGradientBox>
        )}
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <AnimatedRightItem isNewlyGenerated={isNewlyGenerated}>
          <Chip
            bgColor={module.scheduledEndAt ? COLOR_BLUE : COLOR_WHITE}
            borderColor={module.scheduledEndAt ? COLOR_BLUE : COLOR_GREY_6}
            title={
              <Box sx={{ display: "flex", alignItems: "center", gap: "2px" }}>
                <AccessTimeIcon sx={{ fontSize: "16px" }} />
                <Typography variant="bodySRegular" data-text={chipText}>
                  {chipText}
                </Typography>
              </Box>
            }
          />
        </AnimatedRightItem>
        <AnimatedRightItem isNewlyGenerated={isNewlyGenerated}>
          <ModuleStatusBadge module={module} handleStatus={onUpdateStatus} />
        </AnimatedRightItem>
        <AnimatedRightItem isNewlyGenerated={isNewlyGenerated}>
          <DropdownList items={actionItems} />
        </AnimatedRightItem>
      </Box>
    </SpaceBetweenCenter>
  );
};

export default ModuleTileHeader;
